import { useState } from "react";
import { useRouter } from '#app/lib/hooks/router-compat';
import {
  Box,
  Button,
  Icon,
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  Text,
} from "@salestrak/ui";

import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { useAutomationsDialogActions } from "#app/lib/automations-dialog-context";
import { useAnalytics, AnalyticsContext, WorkspaceEvent } from "#app/lib/analytics";
import { useWorkspace, type Workspace } from "#app/lib/data/workspace-context";
import { WorkspaceAvatar } from "#app/components/brand/workspace-avatar";
import { useCanInvite } from "#app/lib/hooks/use-can-invite";
import { useAutomationPermissions } from "#app/hooks/automations/use-automation-permissions";
import { useAutomationsEnabled } from "#app/lib/hooks/use-automations-enabled";
import type { Omittable } from "@salestrak/ui";

type WorkspaceSelectorProps = Omittable<{
  /** Solid pill with tight padding (top-bar style) instead of a fill ghost button. */
  compact?: boolean;
  /** Stretch to fill the available width. */
  fill?: boolean;
  /** Avatar only — hides the workspace name. */
  iconOnly?: boolean;
}>;

export function WorkspaceSelector({
  compact = false,
  fill = false,
  iconOnly = false,
}: WorkspaceSelectorProps = {}) {
  const router = useRouter();
  const { activeWorkspace, workspaces, switchWorkspace } = useWorkspace();
  // Grant-driven (role_invite_grant), consistent with the Members page and
  // the module rail — not a permission key like manage.users.
  const canInvite = useCanInvite();
  const { track } = useAnalytics();
  const { openDialog: openAutomations } = useAutomationsDialogActions();
  // Feature-flagged while automations is being built out, then capability-
  // gated within flagged workspaces (mutations are enforced server-side).
  const automationsEnabled = useAutomationsEnabled();
  const { canOpenManager: canManageAutomations } = useAutomationPermissions();
  const [inviteOpen, setInviteOpen] = useState(false);

  const otherWorkspaces = workspaces.filter(
    (ws) => ws.workspaceId !== activeWorkspace?.workspaceId && ws.memberStatus === "active",
  );

  const handleSwitchWorkspace = async (ws: Workspace) => {
    track({ context: AnalyticsContext.Workspace, event: WorkspaceEvent.Switch });
    const success = await switchWorkspace(ws.workspaceId);
    if (success) router.replace(`/(app)/${ws.urlPath}` as any);
  };

  const avatar = (
    <WorkspaceAvatar
      name={activeWorkspace?.name ?? "Workspace"}
      logo={activeWorkspace?.settings?.logo}
    />
  );
  const label = !iconOnly ? (
    <Box fill="flex" justify="center" minWidth={0}>
      <Text size="sm" truncate weight="semibold">
        {activeWorkspace?.name ?? "Workspace"}
      </Text>
    </Box>
  ) : null;

  // Top-bar pill: solid secondary at the named control height (32px) with
  // tight padding so the avatar hugs the left edge. Off the top bar it is a
  // plain full-width ghost button.
  const trigger = compact ? (
    <Box<"button">
      align="center"
      aria-label="Workspace menu"
      as="button"
      background="secondary"
      className="h-8"
      direction="row"
      fill={fill ? "width" : undefined}
      gap="sm"
      paddingLeft="xs"
      paddingRight={iconOnly ? "xs" : "sm"}
      rounded="md"
    />
  ) : (
    <Button className="w-full justify-start" variant="ghost" />
  );

  return (
    <>
      <Menu>
        <MenuTrigger render={trigger}>
          {avatar}
          {label}
          <Icon name="chevron-down" size="md" />
        </MenuTrigger>
        <MenuPopup align="start" className="min-w-[280px]" side="bottom">
          <MenuGroup>
            <Box align="center" direction="row" gap="sm" paddingX="sm" paddingY="sm">
              <WorkspaceAvatar
                name={activeWorkspace?.name ?? "Workspace"}
                logo={activeWorkspace?.settings?.logo}
                size="default"
              />
              <Box direction="column">
                <Text size="sm" weight="semibold">
                  {activeWorkspace?.name ?? "Workspace"}
                </Text>
                <Text size="xs" variant="muted">
                  {`salestrak.io/${activeWorkspace?.urlPath}`}
                </Text>
              </Box>
            </Box>
          </MenuGroup>

          <MenuGroup>
            <MenuItem onClick={() => router.replace(`/(app)/${activeWorkspace?.urlPath}/settings` as any)}>
              <Icon color="muted" name="settings" size="md" />
              <Text>Settings</Text>
            </MenuItem>
            {automationsEnabled && canManageAutomations && (
              <MenuItem onClick={() => openAutomations()}>
                <Icon color="muted" name="zap" size="md" />
                <Text>Automations</Text>
              </MenuItem>
            )}
            {canInvite && (
              <MenuItem onClick={() => setInviteOpen(true)}>
                <Icon color="muted" name="user-plus" size="md" />
                <Text>Invite members</Text>
              </MenuItem>
            )}
          </MenuGroup>

          <MenuSeparator />

          <MenuGroup>
            <MenuGroupLabel>Switch Workspaces</MenuGroupLabel>

            {otherWorkspaces.map((ws) => (
              <MenuItem key={ws.workspaceId} onClick={() => handleSwitchWorkspace(ws)}>
                <WorkspaceAvatar name={ws.name} logo={ws.settings?.logo} />
                <Box direction="column">
                  <Text size="sm" weight="medium">
                    {ws.name}
                  </Text>
                  <Text size="xs" variant="muted">
                    {`salestrak.io/${ws.urlPath}`}
                  </Text>
                </Box>
              </MenuItem>
            ))}
          </MenuGroup>

          <MenuGroup>
            <MenuItem onClick={() => router.push("/(app)/workspace-select" as any)}>
              <Icon color="muted" name="plus" size="md" />
              <Box direction="column">
                <Text size="sm">Create or join a workspace</Text>
                {workspaces.length > 0 && (
                  <Text size="xs" variant="muted">
                    {workspaces.length} available
                  </Text>
                )}
              </Box>
            </MenuItem>
          </MenuGroup>
        </MenuPopup>
      </Menu>
      {canInvite && <InviteMembersDialog open={inviteOpen} onOpenChange={setInviteOpen} />}
    </>
  );
}
